// Canvas Features Plugin - Grid and Guidelines
import type { GrapesJSEditor } from '../types/grapesjs';

export function registerCanvasFeatures(editor: GrapesJSEditor): void {
  console.log('🎨 Registering canvas features...')
  
  const canvas = editor.Canvas
  const config = editor.getConfig()
  
  // Ensure drag and drop is enabled
  config.dropzone = true
  config.dragMode = 'translate'
  
  // Grid configuration
  const gridConfig = {
    enabled: false,
    size: 20,
    color: 'rgba(0, 123, 255, 0.1)',
    snapToGrid: true
  }

  // Guidelines configuration
  const guideConfig = {
    enabled: false,
    color: 'rgba(0, 123, 255, 0.5)',
    snapToGuidelines: true
  }
  
  // Update grid visibility
  function updateGridVisibility() {
    const canvasEl = canvas.getElement()
    if (!canvasEl) {
      console.log('❌ Canvas element not found')
      return
    }
    
    console.log('🎯 Canvas element found:', canvasEl)
    console.log('🎯 Canvas element classes:', canvasEl.className)
    
    // Try multiple selectors to find the right canvas element
    let canvasWrapper = canvasEl.querySelector('.gjs-cv-canvas') || 
                       canvasEl.querySelector('.gjs-canvas') ||
                       canvasEl.querySelector('[data-gjs-type="wrapper"]') ||
                       canvasEl
    
    // If still not found, try to find any element with canvas-related classes
    if (!canvasWrapper || canvasWrapper === canvasEl) {
      const possibleElements = canvasEl.querySelectorAll('*')
      for (let el of possibleElements) {
        if (el.className && (el.className.includes('canvas') || el.className.includes('gjs-cv'))) {
          canvasWrapper = el
          break
        }
      }
    }
    
    console.log('🎯 Canvas wrapper found:', canvasWrapper)
    console.log('🎯 Canvas wrapper classes:', canvasWrapper.className)
    console.log('🎯 Canvas wrapper style:', canvasWrapper.style.cssText)
    
    if (gridConfig.enabled) {
      canvasWrapper.style.backgroundImage = `
        linear-gradient(${gridConfig.color} 1px, transparent 1px),
        linear-gradient(90deg, ${gridConfig.color} 1px, transparent 1px)
      `
      canvasWrapper.style.backgroundSize = `${gridConfig.size}px ${gridConfig.size}px`
      canvasWrapper.style.backgroundPosition = '0 0'
      console.log('✅ Grid enabled - background applied to:', canvasWrapper)
      console.log('✅ Grid color:', gridConfig.color)
      console.log('✅ Grid size:', gridConfig.size)
    } else {
      canvasWrapper.style.backgroundImage = 'none'
      console.log('✅ Grid disabled - background removed from:', canvasWrapper)
    }
  }
  
  // Snap to grid functionality
  function snapToGrid(value) {
    if (!gridConfig.snapToGrid || !gridConfig.enabled) return value
    return Math.round(value / gridConfig.size) * gridConfig.size
  }
  
  // Guidelines functionality
  let guidelines = []

  // Helper: normalize a value to a DOM element if possible
  function getDomElement(value: any): HTMLElement | null {
    if (!value) return null
    // Already a DOM element
    if (value instanceof Element) return value as HTMLElement
    // GrapesJS component model exposing getEl()
    if (typeof value.getEl === 'function') {
      const el = value.getEl()
      return el instanceof Element ? (el as HTMLElement) : null
    }
    // Some components expose a view property with el
    if (value.view && value.view.el) {
      return value.view.el instanceof Element ? value.view.el : null
    }
    return null
  }

  function createGuideline(element: any, type: string) {
    const el = getDomElement(element)
    if (!el) return null

    const rect = el.getBoundingClientRect()
    const canvasEl = canvas.getElement()
    const canvasRect = canvasEl ? canvasEl.getBoundingClientRect() : { top: 0, left: 0 }

    if (type === 'horizontal') {
      return {
        type: 'horizontal',
        position: rect.top - canvasRect.top + rect.height / 2,
        element: el
      }
    } else {
      return {
        type: 'vertical',
        position: rect.left - canvasRect.left + rect.width / 2,
        element: el
      }
    }
  }
  
  function showGuidelines() {
    if (!guideConfig.enabled) return
    
    // Clear existing guidelines
    clearGuidelines()
    
    const selected = editor.getSelected()
    if (!selected) return

    const selEl = getDomElement(selected)
    if (!selEl) return
    const selectedRect = selEl.getBoundingClientRect()

    const allComponents = editor.DomComponents.getWrapper().find('*')

    allComponents.forEach(component => {
      if (component === selected) return

      const compEl = getDomElement(component)
      if (!compEl) return

      const rect = compEl.getBoundingClientRect()

      // Horizontal alignment
      if (Math.abs(rect.top - selectedRect.top) < 10) {
        const g = createGuideline(component, 'horizontal')
        if (g) guidelines.push(g)
      }
      if (Math.abs(rect.bottom - selectedRect.bottom) < 10) {
        const g = createGuideline(component, 'horizontal')
        if (g) guidelines.push(g)
      }
      if (Math.abs(rect.top + rect.height / 2 - selectedRect.top - selectedRect.height / 2) < 10) {
        const g = createGuideline(component, 'horizontal')
        if (g) guidelines.push(g)
      }

      // Vertical alignment
      if (Math.abs(rect.left - selectedRect.left) < 10) {
        const g = createGuideline(component, 'vertical')
        if (g) guidelines.push(g)
      }
      if (Math.abs(rect.right - selectedRect.right) < 10) {
        const g = createGuideline(component, 'vertical')
        if (g) guidelines.push(g)
      }
      if (Math.abs(rect.left + rect.width / 2 - selectedRect.left - selectedRect.width / 2) < 10) {
        const g = createGuideline(component, 'vertical')
        if (g) guidelines.push(g)
      }
    })
    
    renderGuidelines()
  }
  
  function renderGuidelines() {
    const canvasEl = canvas.getElement()
    if (!canvasEl) return
    
    guidelines.forEach(guide => {
      const guideEl = document.createElement('div')
      guideEl.className = `gjs-guide gjs-guide-${guide.type === 'horizontal' ? 'h' : 'v'}`
      guideEl.style.cssText = `
        position: absolute;
        background: ${guideConfig.color};
        z-index: 999;
        ${guide.type === 'horizontal' ? 
          `height: 1px; width: 100%; top: ${guide.position}px;` : 
          `width: 1px; height: 100%; left: ${guide.position}px;`
        }
      `
      canvasEl.appendChild(guideEl)
    })
  }
  
  function clearGuidelines() {
    const canvasEl = canvas.getElement()
    if (!canvasEl) return
    
    const guides = canvasEl.querySelectorAll('.gjs-guide')
    guides.forEach(guide => guide.remove())
    guidelines = []
  }
  
  // Initialize canvas features
  function initCanvasFeatures() {
    console.log('🎨 Initializing canvas features...')
    
    // Wait for canvas to be ready
    setTimeout(() => {
      console.log('🎨 Canvas ready, adding features...')
      
      // Ensure drag and drop is properly configured
      const canvasEl = canvas.getElement()
      if (canvasEl) {
        // Make sure the canvas is a drop zone
        canvasEl.setAttribute('data-highlightable', 'true')
        canvasEl.style.pointerEvents = 'auto'
        
        // Ensure the canvas wrapper is also a drop zone
        const canvasWrapper = canvasEl.querySelector('.gjs-cv-canvas') || canvasEl
        if (canvasWrapper) {
          canvasWrapper.setAttribute('data-highlightable', 'true')
          canvasWrapper.style.pointerEvents = 'auto'
        }
        
        console.log('✅ Canvas drop zone configured')
      }
      
      updateGridVisibility()
      
      // Listen for component selection
      editor.on('component:selected', showGuidelines)
      editor.on('component:deselected', clearGuidelines)
      editor.on('component:update', showGuidelines)
      
      // Listen for component movement
      editor.on('component:drag', (component, event) => {
        if (gridConfig.snapToGrid && gridConfig.enabled) {
          const el = getDomElement(component)
          if (!el) return
          const rect = el.getBoundingClientRect()
          const newLeft = snapToGrid(rect.left)
          const newTop = snapToGrid(rect.top)
          
          if (newLeft !== rect.left || newTop !== rect.top) {
            component.setStyle({
              left: newLeft + 'px',
              top: newTop + 'px'
            })
          }
        }
      })
      
      // Listen for block drops
      // NOTE: block:drag:stop fires as (cmp, block) — this handler only
      // receives the first argument, the newly-created component, not the
      // block. It's null on any drop that doesn't produce a component
      // (confirmed via live repro: a second, identical unguarded listener
      // in TemplateBuilder.jsx threw "Cannot read properties of null
      // (reading 'get')" here and aborted GrapesJS's own internal
      // __endDrag processing mid-drop, silently breaking every block
      // drag). Guard with optional chaining instead of assuming non-null.
      editor.on('block:drag:stop', (block) => {
        console.log('🎯 Block dropped:', block?.get('label'))
      })

      editor.on('component:add', (component) => {
        console.log('🎯 Component added:', component?.get('tagName'))
      })
      
      console.log('✅ Canvas features initialized')
    }, 2000) // Increased timeout to ensure canvas is fully loaded
  }
  
  // Initialize when editor is ready
  if (editor.isReady) {
    console.log('🎨 Editor is ready, initializing canvas features...')
    initCanvasFeatures()
  } else {
    console.log('🎨 Waiting for editor to load...')
    editor.on('load', () => {
      console.log('🎨 Editor loaded, initializing canvas features...')
      initCanvasFeatures()
    })
  }
  
  // Add commands for grid control
  editor.Commands.add('toggle-grid', {
    run: (editor) => {
      console.log('🎯 Toggle grid command executed')
      gridConfig.enabled = !gridConfig.enabled
      updateGridVisibility()
      console.log(`Grid ${gridConfig.enabled ? 'enabled' : 'disabled'}`)
    }
  })
  
  editor.Commands.add('set-grid-size', {
    run: (editor, sender, options) => {
      if (options.size) {
        gridConfig.size = options.size
        updateGridVisibility()
        console.log(`Grid size set to ${options.size}px`)
      }
    }
  })
  
  console.log('✅ Canvas features registered')
} 